/**
 * `DocumentMeta` — le titre, la nature et la provenance du document, dits une
 * seule fois (#1333).
 *
 * ## Pourquoi ce module existe
 *
 * Les mêmes trois faits étaient calculés à **trois** endroits, sur des sources
 * partiellement différentes, et divergeaient donc :
 *
 * | Surface | Titre | Nature |
 * |---|---|---|
 * | Le bandeau du panneau | renommage → méta → titre du job | `draftKind`, calculé dans le rendu |
 * | Le contexte agent (`canvasAgentState`) | renommage → méta, **sans le titre du job** | règle recopiée « sur les mêmes sources, sans hook » |
 * | Le brouillon persisté (`useCanvasAutoSave`) | la chaîne du panneau | `draftKind` du panneau |
 *
 * Le contexte agent annonçait donc `titre: null` sur l'acte que le panneau
 * nommait et que le PUT enregistrait sous son nom. La règle du panneau vivait
 * en outre dans du JSX, donc hors de portée de toute garde.
 *
 * ## Ce qui décide, et dans quel ordre
 *
 * Une seule chaîne, appliquée à la présentation du **scope de la conversation**
 * — jamais à une projection d'écran :
 *
 * 1. le **renommage** de l'avocat (`presentation.documentTitle`) prime sur tout,
 *    y compris sur un événement de job qui arriverait après ;
 * 2. sinon les **métadonnées du document chargé** (`templateMeta`) ;
 * 3. sinon le **titre validé au lancement de l'acte**, conservé par
 *    `redactionStreamHub` hors de React — c'est ce maillon qui manquait au
 *    contexte agent.
 *
 * ⚠️ **Aucun repli sur un libellé d'écran** (« Nouveau document », « Acte
 * généré… ») : ce sont des phrases d'interface, pas des noms. Les envoyer ferait
 * croire au modèle qu'un document nommé est ouvert, et les enregistrerait comme
 * titre de brouillon. Jamais renommé ⟹ `titre: null`, et c'est l'appelant qui
 * choisit son libellé de secours.
 *
 * ⚠️ **Un titre blanc n'est pas un titre** : la chaîne est `trim`ée et un reste
 * vide retombe sur le maillon suivant.
 */

import { CANVAS_DRAFT_DEFAULT_TITLE } from '@hector/shared-types';
import type { CanvasDraftKind, CanvasDocumentSource } from '@hector/shared-types';
import { redactionStreamHub } from '../streaming/redactionStreamHub';
import { canvasDocumentRuntime } from './canvasDocumentRuntime';
import type { CanvasDocumentPresentationSnapshot } from './canvasDocumentScope';
import { isTemplateHeldByCanvasHolder } from './canvasHolder';

/** Ce qu'un document est, pour toutes ses surfaces à la fois. */
export interface CanvasDocumentMeta {
  /** Le nom du document, ou `null` s'il n'en a jamais reçu. */
  readonly titre: string | null;
  /** La nature persistée du brouillon — c'est elle qui part dans le PUT. */
  readonly nature: CanvasDraftKind;
  /** La même nature, dans le vocabulaire que le contexte agent expose au modèle. */
  readonly provenance: CanvasDocumentSource;
  /** Le dossier du DOCUMENT, jamais celui de l'écran affiché. */
  readonly caseId: string | null;
  /**
   * La pièce d'origine, pour le round-trip d'un brouillon importé : celle du
   * brouillon restauré, sinon le `documentId` d'un document de nature
   * `imported` — jamais celui d'un document généré ou vierge.
   */
  readonly sourceDocumentId: string | null;
  /** Le brouillon durable déjà attaché à ce document, s'il existe. */
  readonly restoredDraftId: string | null;
  /** L'ACK d'intégrité exact du brouillon restauré, s'il est connu. */
  readonly restoredDraftIntegrityAttemptId: string | null;
  /**
   * Les métadonnées du gabarit **retenues** pour ce document.
   *
   * `null` quand `templateMeta` déclare une autre conversation : la garde
   * fail-closed de `canvasHolder` reste en place, elle est simplement appliquée
   * ici une fois pour toutes au lieu d'être recopiée par chaque consommateur.
   */
  readonly templateMeta: CanvasDocumentPresentationSnapshot['templateMeta'];
  /**
   * Le gabarit publié a été REFUSÉ parce qu'il déclare une autre conversation.
   *
   * ⚠️ À lire au lieu de retester `templateMeta === null`, qui confond deux
   * situations opposées : « aucun gabarit » et « gabarit d'autrui neutralisé ».
   * L'appelant qui les confondait montait le blob d'un document étranger dans
   * le premier cas ; ce verdict est le même que celui appliqué ci-dessus, rendu
   * une seule fois, pour qu'aucune surface n'ait à le redériver.
   */
  readonly gabaritEtranger: boolean;
}

/** Ce que le bandeau du panneau affiche comme nom de document. */
export function libelleDuBandeau(meta: CanvasDocumentMeta, libelleDeSecours: string): string {
  return meta.titre ?? libelleDeSecours;
}

/** Ce que le PUT du brouillon enregistre — les champs qui viennent du document. */
export interface CanvasDraftIdentityFields {
  readonly title: string;
  readonly kind: CanvasDraftKind;
  readonly caseId: string | null;
  readonly sourceDocumentId: string | null;
  readonly restoredDraftId: string | null;
  readonly restoredDraftIntegrityAttemptId: string | null;
}

/**
 * Le brouillon persisté, dérivé du MÊME meta que le bandeau et le contexte agent.
 *
 * Cette dérivation vivait dans les props JSX de `RedactionPanel` — donc hors de
 * portée de toute garde, alors que c'est elle qui décide sous quel nom, quelle
 * nature et surtout quel DOSSIER le travail de l'avocat est enregistré.
 *
 * @param dossierAffiche Le dossier de l'écran, **défaut seulement** : le dossier
 * du document prime (#1340). La règle inverse rangeait trois brouillons de trois
 * conversations sous le même `case_id` après une bascule d'écran (incident du
 * 2026-08-30).
 */
export function brouillonDuDocument(
  meta: CanvasDocumentMeta,
  dossierAffiche: string | null,
): CanvasDraftIdentityFields {
  return {
    // « Document sans titre » est le défaut du schéma partagé, pas un nom
    // choisi : il ne se substitue qu'au moment d'écrire, jamais dans le meta.
    title: meta.titre ?? CANVAS_DRAFT_DEFAULT_TITLE,
    kind: meta.nature,
    caseId: meta.caseId ?? dossierAffiche ?? null,
    sourceDocumentId: meta.sourceDocumentId,
    restoredDraftId: meta.restoredDraftId,
    restoredDraftIntegrityAttemptId: meta.restoredDraftIntegrityAttemptId,
  };
}

const NATURE_VERS_PROVENANCE: Record<CanvasDraftKind, CanvasDocumentSource> = {
  imported: 'charge',
  generated: 'genere',
  blank: 'vierge',
};

/**
 * Un nom, ou `null` — et « Document sans titre » n'est PAS un nom.
 *
 * ⚠️ **C'est le maillon qui manquait, et il a éteint le nommage automatique
 * dès le premier rechargement de page** (corrigé le 2026-09-22). La
 * présentation du scope exclut déjà soigneusement ce défaut
 * (`canvasDocumentScope.ts`), mais le maillon SUIVANT de la chaîne —
 * `templateMeta.documentTitle` — porte le titre du brouillon TEL QUEL, défaut
 * compris. Un brouillon jamais nommé, une fois rouvert, rendait donc
 * `titre: 'Document sans titre'` : le panneau croyait le document nommé et ne
 * le nommait plus jamais, et le contexte agent annonçait au modèle un document
 * nommé qui ne l'était pas.
 *
 * Conséquence assumée : un avocat qui renommerait délibérément son document
 * « Document sans titre » le verrait se faire renommer automatiquement. Le cas
 * est théorique, et le coût de l'inverse est une liste de documents
 * indiscernables.
 */
function titreNettoye(valeur: string | null | undefined): string | null {
  const nettoye = valeur?.trim() ?? '';
  if (nettoye.length === 0 || nettoye === CANVAS_DRAFT_DEFAULT_TITLE) return null;
  return nettoye;
}

/**
 * Décision PURE : aucun accès au runtime, au réseau ni à React.
 *
 * @param presentation Le document publié par le scope, ou `null` s'il n'a rien publié.
 * @param conversationId Le propriétaire du scope, pour la garde de gabarit étranger.
 * @param titreDuTravail Le titre validé au lancement du dernier acte de ce document.
 */
export function deciderLeMetaDuDocument(input: {
  readonly presentation: CanvasDocumentPresentationSnapshot | null;
  readonly conversationId: string | null;
  readonly titreDuTravail: string | null;
  readonly aUnTravailDeRedaction: boolean;
}): CanvasDocumentMeta {
  const { presentation, conversationId, titreDuTravail, aUnTravailDeRedaction } = input;
  const gabaritPublie = presentation?.templateMeta ?? null;
  const gabaritRetenu = isTemplateHeldByCanvasHolder(
    gabaritPublie?.conversationId,
    conversationId,
  );
  const templateMeta = gabaritRetenu ? gabaritPublie : null;
  const activeDraft = presentation?.activeDraft ?? null;

  // `activeDraft` PRIME : un brouillon 'imported' rouvert doit se re-sauver
  // 'imported', même si `sourceFilePath` est nul après le round-trip.
  const nature: CanvasDraftKind = activeDraft?.kind
    ?? templateMeta?.kind
    ?? (templateMeta?.sourceFilePath
      ? 'imported'
      : (aUnTravailDeRedaction ? 'generated' : 'blank'));

  return {
    titre: titreNettoye(presentation?.documentTitle)
      ?? titreNettoye(templateMeta?.documentTitle)
      ?? titreNettoye(titreDuTravail),
    nature,
    provenance: NATURE_VERS_PROVENANCE[nature],
    // Le dossier suit le DOCUMENT (incident du 2026-08-30 : trois brouillons de
    // trois conversations rangés sous le même `case_id` après une bascule, parce
    // que l'auto-save lisait le dossier affiché en premier). Le dossier de
    // l'écran n'est plus qu'un défaut, appliqué par l'appelant quand ce champ
    // est nul — `RedactionPanel`, au câblage de l'auto-save.
    caseId: activeDraft?.caseId ?? templateMeta?.caseId ?? null,
    // La source suit la NATURE résolue ci-dessus : un document importé a pour
    // source son `documentId` (#1354/D8). La règle précédente ne connaissait que
    // `sourceFilePath`, un champ FACULTATIF de `canvas_load_template_request` :
    // une pièce chargée par l'agent sans chemin S3 s'enregistrait `imported`
    // mais sans source, et le brouillon perdait le lien vers sa pièce (gate
    // `ws-mocked` du 2026-09-10). Relire `nature` plutôt que refaire un test sur
    // les champs du gabarit, c'est garder UNE règle pour les deux champs du PUT :
    // hors brouillon restauré, un document n'a de source que s'il est importé.
    // ⚠️ `activeDraft` PRIME : un brouillon restauré porte déjà sa source.
    sourceDocumentId: activeDraft?.sourceDocumentId
      ?? (nature === 'imported' ? (templateMeta?.documentId ?? null) : null),
    restoredDraftId: activeDraft?.id ?? null,
    restoredDraftIntegrityAttemptId: activeDraft?.integrityAttemptId ?? null,
    templateMeta,
    gabaritEtranger: gabaritPublie !== null && !gabaritRetenu,
  };
}

/**
 * Le meta du document d'une conversation, lu hors de React.
 *
 * ⚠️ **Ce lecteur prenait DEUX paramètres, et ils ont fusionné** (#1336) : la
 * clé du scope et le propriétaire attendu. Depuis que la conversation EST
 * l'identité du document logique, les passer séparément aurait exigé de
 * l'appelant qu'il annonce deux fois la même valeur — et l'aurait laissé
 * introduire une divergence que rien ne rattrape.
 *
 * La garde de gabarit étranger reste une vraie vérification : elle confronte le
 * propriétaire DÉCLARÉ par les métadonnées du gabarit chargé — une entrée, pas
 * une vérité (#862) — à la conversation qui possède réellement ce document.
 *
 * Le travail de rédaction est relu sur le hub, la seule mémoire du flux : un
 * acte lancé porte son titre bien avant qu'un brouillon existe, et une écoute
 * survit à la navigation qui démonterait un état React.
 */
export function lireLeMetaDuScope(conversationId: string): CanvasDocumentMeta {
  const travail = redactionStreamHub.derniereEcoutePourConversation(conversationId);
  return deciderLeMetaDuDocument({
    presentation: canvasDocumentRuntime.getScope(conversationId)?.getPresentation() ?? null,
    conversationId,
    titreDuTravail: travail?.documentTitle ?? null,
    aUnTravailDeRedaction: travail !== null,
  });
}
